import React, { useEffect, useState } from 'react'
import {
    Box,
    Card,
    CardContent,
    Container,
    Divider,
    Grid,
    IconButton,
    Skeleton,
    Typography,
} from '@mui/material'
import {
    ArrowBack,
    Email,
    LocationOn,
    Person,
    Phone,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { useSnackbar } from 'notistack'
import { api } from '../../../../services/Main/Api'
import { formatCpfCnpj, formatPhone } from '../../../../helpers/formatters'

const TYPE_MAP = {
    CLIENT: { label: 'Cliente', color: '#3B82F6' },
    EMPLOYEE: { label: 'Funcionário', color: '#8B5CF6' },
    SUPPLIER: { label: 'Fornecedor', color: '#F59E0B' },
    BUSINESS_PARTNER: { label: 'Sócio', color: '#22C55E' },
}

const InfoRow = ({ label, value, icon }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {icon && <Box sx={{ color: '#94A3B8', display: 'flex' }}>{icon}</Box>}
        <Typography variant="body2" sx={{ color: '#94A3B8', minWidth: 120 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500 }}>
            {value || '—'}
        </Typography>
    </Box>
)

const SectionTitle = ({ children }) => (
    <Typography
        variant="body2"
        sx={{
            color: '#94A3B8',
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '0.7rem',
            letterSpacing: '0.05em',
            mb: 2,
        }}
    >
        {children}
    </Typography>
)

const ContactDetail = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()

    const [contact, setContact] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        setLoading(true)
        api.get(`unit/${id}/`)
            .then((res) => setContact(res.data))
            .catch(() => {
                enqueueSnackbar('Erro ao carregar contato.', { variant: 'error' })
            })
            .finally(() => setLoading(false))
    }, [id])

    const typeInfo = TYPE_MAP[contact?.contact_type] || { label: contact?.contact_type, color: '#94A3B8' }

    const formatAddress = () => {
        const parts = [
            contact?.street,
            contact?.address_number && `nº ${contact.address_number}`,
            contact?.complement,
        ].filter(Boolean)

        const line2 = [
            contact?.district,
            contact?.city,
            contact?.state,
        ].filter(Boolean)

        if (parts.length === 0 && line2.length === 0) return null
        return { line1: parts.join(', '), line2: line2.join(' - '), cep: contact?.cep }
    }

    const address = contact ? formatAddress() : null

    return (
        <>
            <Helmet>
                <title>{contact?.name ? `${contact.name} - Contato` : 'Contato'} - Bloco B3</title>
            </Helmet>
            <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>
                <Container maxWidth="lg" sx={{ py: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
                        <IconButton onClick={() => navigate('/financeiro/contatos')}>
                            <ArrowBack />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                    {loading ? <Skeleton width={250} /> : contact?.name}
                                </Typography>
                                {!loading && contact?.contact_type && (
                                    <Box
                                        sx={{
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 1.5,
                                            bgcolor: `${typeInfo.color}14`,
                                            border: `1px solid ${typeInfo.color}30`,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{ color: typeInfo.color, fontWeight: 600 }}
                                        >
                                            {typeInfo.label}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {loading ? (
                        <Skeleton variant="rounded" height={400} animation="pulse" />
                    ) : contact ? (
                        <Grid container spacing={2}>
                            {/* Dados Pessoais */}
                            <Grid item xs={12} md={6}>
                                <Card
                                    sx={{
                                        border: '1px solid #E2E8F0',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                        borderRadius: 2,
                                        height: '100%',
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <SectionTitle>Dados do Contato</SectionTitle>

                                        <InfoRow
                                            icon={<Person sx={{ fontSize: 18 }} />}
                                            label="Nome"
                                            value={contact.name}
                                        />
                                        <InfoRow
                                            label="CPF/CNPJ"
                                            value={contact.cpf ? formatCpfCnpj(contact.cpf) : null}
                                        />

                                        <Divider sx={{ my: 2 }} />

                                        <InfoRow
                                            icon={<Email sx={{ fontSize: 18 }} />}
                                            label="Email"
                                            value={contact.email}
                                        />
                                        <InfoRow
                                            icon={<Phone sx={{ fontSize: 18 }} />}
                                            label="Telefone"
                                            value={contact.phone ? formatPhone(contact.phone) : null}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Endereço */}
                            <Grid item xs={12} md={6}>
                                <Card
                                    sx={{
                                        border: '1px solid #E2E8F0',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                        borderRadius: 2,
                                        height: '100%',
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <SectionTitle>Endereço</SectionTitle>

                                        {address ? (
                                            <>
                                                <InfoRow
                                                    icon={<LocationOn sx={{ fontSize: 18 }} />}
                                                    label="Logradouro"
                                                    value={address.line1}
                                                />
                                                <InfoRow
                                                    label="Bairro / Cidade"
                                                    value={address.line2}
                                                />
                                                {address.cep && (
                                                    <InfoRow
                                                        label="CEP"
                                                        value={address.cep}
                                                    />
                                                )}
                                            </>
                                        ) : (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    py: 4,
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                                                    Nenhum endereço cadastrado
                                                </Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    ) : (
                        <Card sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                                Contato não encontrado
                            </Typography>
                        </Card>
                    )}
                </Container>
            </Box>
        </>
    )
}

export default ContactDetail
